import { useContext, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Alert,
  TextField,
  IconButton,
  Chip,
  alpha,
  InputAdornment,
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from '../../../lib/axios';
import { useVariants, useUpdateVariant } from '../../../hooks/useVariants';
import type { Product, ProductVariant } from '../../../types';
import { ColorModeContext } from '../../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../../theme/colors';
import { useSnackbar } from '../../../components/SnackbarProvider';

interface VariantPricingTabProps {
  productId: string;
}

export const VariantPricingTab = ({ productId }: VariantPricingTabProps) => {
  const { mode } = useContext(ColorModeContext);
  const { showMessage } = useSnackbar();
  const [editingVariantId, setEditingVariantId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<number>(0);

  // Fetch product for base price
  const { data: product } = useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      const response = await axiosInstance.get<Product>(`/products/${productId}`);
      return response.data;
    },
    enabled: !!productId,
  });

  // Fetch variants
  const { data: variants = [], isLoading, error } = useVariants(productId);

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const formatAttributes = (attributes: Record<string, unknown>): string => {
    return Object.entries(attributes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  const handleStartEdit = (variant: ProductVariant) => {
    setEditingVariantId(variant.id);
    setEditValue(variant.priceAdjustment);
  };

  const handleCancelEdit = () => {
    setEditingVariantId(null);
    setEditValue(0);
  };

  const updateVariantMutation = useUpdateVariant(productId, editingVariantId || '');

  const handleSaveEdit = async (variantId: string) => {
    try {
      await updateVariantMutation.mutateAsync({
        priceAdjustment: editValue,
      });
      showMessage('Price adjustment updated successfully', 'success');
      setEditingVariantId(null);
      setEditValue(0);
    } catch (error: any) {
      showMessage(
        error.response?.data?.message || 'Failed to update price adjustment',
        'error'
      );
    }
  };

  return (
    <Card
      sx={{
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: theme.radius.md,
      }}
    >
      <CardContent>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonetizationOnIcon sx={{ color: palette.primary, fontSize: 28 }} />
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                textTransform: 'uppercase',
              }}
            >
              Variant Pricing
            </Typography>
          </Box>

          {product && (
            <Chip
              label={`Base Price: ${formatPrice(product.price)}`}
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.bold,
                backgroundColor: alpha(palette.primary, 0.1),
                color: palette.primary,
                border: `1px solid ${palette.primary}`,
              }}
            />
          )}
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={60}
                sx={{
                  mb: 1,
                  borderRadius: theme.radius.sm,
                  backgroundColor: colors.surfaceElevated,
                }}
              />
            ))}
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert
            severity="error"
            sx={{
              borderRadius: theme.radius.sm,
              fontFamily: typography.fontFamily.body,
            }}
          >
            Failed to load variants. Please try again.
          </Alert>
        )}

        {/* Empty State */}
        {!isLoading && !error && variants.length === 0 && (
          <Alert
            severity="info"
            sx={{
              borderRadius: theme.radius.sm,
              fontFamily: typography.fontFamily.body,
            }}
          >
            No variants found. Create variants to manage their pricing.
          </Alert>
        )}

        {/* Variants Table */}
        {!isLoading && !error && variants.length > 0 && product && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: colors.surfaceElevated,
                  }}
                >
                  <TableCell
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.bold,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.widest,
                      color: colors.textMuted,
                      borderBottom: `2px solid ${colors.border}`,
                    }}
                  >
                    SKU
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.bold,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.widest,
                      color: colors.textMuted,
                      borderBottom: `2px solid ${colors.border}`,
                    }}
                  >
                    Attributes
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.bold,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.widest,
                      color: colors.textMuted,
                      borderBottom: `2px solid ${colors.border}`,
                    }}
                  >
                    Base Price
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.bold,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.widest,
                      color: colors.textMuted,
                      borderBottom: `2px solid ${colors.border}`,
                    }}
                  >
                    Price Adjustment
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.bold,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.widest,
                      color: colors.textMuted,
                      borderBottom: `2px solid ${colors.border}`,
                    }}
                  >
                    Final Price
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.bold,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.widest,
                      color: colors.textMuted,
                      borderBottom: `2px solid ${colors.border}`,
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: typography.fontFamily.mono,
                      fontWeight: typography.fontWeight.bold,
                      fontSize: typography.fontSize.xs,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.widest,
                      color: colors.textMuted,
                      borderBottom: `2px solid ${colors.border}`,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {variants.map((variant) => {
                  const isEditing = editingVariantId === variant.id;
                  const finalPrice = product.price + variant.priceAdjustment;
                  const currentEditValue = isEditing ? editValue : variant.priceAdjustment;

                  return (
                    <TableRow
                      key={variant.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: alpha(colors.surfaceElevated, 0.5),
                        },
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <TableCell
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.sm,
                          color: colors.textPrimary,
                          borderBottom: `1px solid ${colors.borderLight}`,
                        }}
                      >
                        {variant.sku}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: typography.fontFamily.body,
                          fontSize: typography.fontSize.sm,
                          color: colors.textSecondary,
                          borderBottom: `1px solid ${colors.borderLight}`,
                        }}
                      >
                        {formatAttributes(variant.attributes)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.sm,
                          color: colors.textSecondary,
                          borderBottom: `1px solid ${colors.borderLight}`,
                        }}
                      >
                        {formatPrice(product.price)}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: `1px solid ${colors.borderLight}`,
                        }}
                      >
                        {isEditing ? (
                          <TextField
                            type="number"
                            value={currentEditValue}
                            onChange={(e) => setEditValue(parseFloat(e.target.value) || 0)}
                            size="small"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">$</InputAdornment>
                              ),
                              style: {
                                fontFamily: typography.fontFamily.mono,
                                fontSize: typography.fontSize.sm,
                              },
                            }}
                            sx={{
                              width: 120,
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: colors.surface,
                                '& fieldset': {
                                  borderColor: palette.primary,
                                },
                              },
                            }}
                          />
                        ) : (
                          <Typography
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.sm,
                              color:
                                variant.priceAdjustment > 0
                                  ? palette.success
                                  : variant.priceAdjustment < 0
                                  ? palette.error
                                  : colors.textSecondary,
                              fontWeight: typography.fontWeight.medium,
                            }}
                          >
                            {variant.priceAdjustment >= 0 ? '+' : ''}
                            {formatPrice(variant.priceAdjustment)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.md,
                          fontWeight: typography.fontWeight.bold,
                          color: palette.primary,
                          borderBottom: `1px solid ${colors.borderLight}`,
                        }}
                      >
                        {formatPrice(
                          isEditing ? product.price + currentEditValue : finalPrice
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: `1px solid ${colors.borderLight}`,
                        }}
                      >
                        <Chip
                          label={variant.isActive ? 'Active' : 'Inactive'}
                          color={variant.isActive ? 'success' : 'default'}
                          size="small"
                          sx={{
                            fontFamily: typography.fontFamily.mono,
                            fontWeight: typography.fontWeight.medium,
                            fontSize: typography.fontSize.xs,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          borderBottom: `1px solid ${colors.borderLight}`,
                        }}
                      >
                        {isEditing ? (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleSaveEdit(variant.id)}
                              disabled={updateVariantMutation.isPending}
                              sx={{
                                color: palette.success,
                                '&:hover': {
                                  backgroundColor: alpha(palette.success, 0.1),
                                },
                              }}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCancelEdit}
                              disabled={updateVariantMutation.isPending}
                              sx={{
                                color: palette.error,
                                '&:hover': {
                                  backgroundColor: alpha(palette.error, 0.1),
                                },
                              }}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => handleStartEdit(variant)}
                            sx={{
                              color: palette.primary,
                              '&:hover': {
                                backgroundColor: alpha(palette.primary, 0.1),
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Summary */}
        {!isLoading && !error && variants.length > 0 && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: theme.radius.sm,
              backgroundColor: colors.surfaceElevated,
              border: `1px solid ${colors.border}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                letterSpacing: typography.letterSpacing.widest,
                color: colors.textMuted,
              }}
            >
              Summary: {variants.length} variant{variants.length !== 1 ? 's' : ''} •{' '}
              {variants.filter((v) => v.isActive).length} active •{' '}
              Price range:{' '}
              {product &&
                formatPrice(
                  Math.min(...variants.map((v) => product.price + v.priceAdjustment))
                )}{' '}
              -{' '}
              {product &&
                formatPrice(
                  Math.max(...variants.map((v) => product.price + v.priceAdjustment))
                )}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
